import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { publicOptionApi } from '@/lib/api'
import { Plus, Edit2, Trash2, Loader2, FileSearch, Search, ChevronLeft, ChevronRight } from 'lucide-react'

export function PublicOptionListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [events, setEvents] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [showDialog, setShowDialog] = useState(false)
  const [editingEvent, setEditingEvent] = useState<any>(null)
  const [form, setForm] = useState({
    eventname: '',
    eventkeywords: '',
    eventstarttime: '',
    eventendtime: '',
    eventstopwords: '',
  })

  const loadEvents = useCallback(async () => {
    setLoading(true)
    try {
      const res: any = await publicOptionApi.list(page, searchKeyword)
      const list = res?.data || res || []
      setEvents(Array.isArray(list) ? list : [])
      setTotal(res?.total || (Array.isArray(list) ? list.length : 0))
    } catch (err) {
      console.error('Failed to load events:', err)
    } finally {
      setLoading(false)
    }
  }, [page, searchKeyword])

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  const handleSearch = () => {
    setPage(1)
    loadEvents()
  }

  const statusBadge = (status: number) => {
    if (status === 0) return { variant: 'warning' as const, text: 'statusCreating' }
    if (status === 1) return { variant: 'success' as const, text: 'statusSuccess' }
    return { variant: 'destructive' as const, text: 'statusFailed' }
  }

  const handleSave = async () => {
    if (!form.eventname.trim()) return
    try {
      if (editingEvent) {
        await publicOptionApi.update({ ...form, id: editingEvent.id })
      } else {
        await publicOptionApi.create(form)
      }
      setShowDialog(false)
      loadEvents()
    } catch (err) {
      console.error('Failed to save event:', err)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm(t('monitor.deleteConfirm'))) return
    try {
      await publicOptionApi.delete(id)
      loadEvents()
    } catch (err) {
      console.error('Failed to delete event:', err)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t('publicOption.title')}</h2>
        <Button onClick={() => {
          setEditingEvent(null)
          setForm({ eventname: '', eventkeywords: '', eventstarttime: '', eventendtime: '', eventstopwords: '' })
          setShowDialog(true)
        }}>
          <Plus className="mr-2 h-4 w-4" />
          {t('publicOption.addEvent')}
        </Button>
      </div>

      {/* Search bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder={t('common.search')}
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-64"
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch() }}
            />
            <Button onClick={handleSearch} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
              {t('common.search')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('publicOption.eventName')}</TableHead>
                <TableHead>{t('publicOption.eventKeywords')}</TableHead>
                <TableHead>{t('publicOption.eventStartTime')}</TableHead>
                <TableHead>{t('publicOption.eventEndTime')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : events.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    {t('common.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => {
                  const badge = statusBadge(event.status)
                  return (
                    <TableRow 
                      key={event.id}
                      className="cursor-pointer hover:bg-accent/50"
                      onClick={() => { window.location.href = `/publicoption/${event.id}` }}
                    >
                      <TableCell className="font-medium">{event.eventname}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{event.eventkeywords}</TableCell>
                      <TableCell className="text-muted-foreground">{event.eventstarttime}</TableCell>
                      <TableCell className="text-muted-foreground">{event.eventendtime}</TableCell>
                      <TableCell>
                        <Badge variant={badge.variant}>{t(`publicOption.${badge.text}`)}</Badge>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon"
                            onClick={() => {
                              setEditingEvent(event)
                              setForm({
                                eventname: event.eventname,
                                eventkeywords: event.eventkeywords,
                                eventstarttime: event.eventstarttime,
                                eventendtime: event.eventendtime,
                                eventstopwords: event.eventstopwords,
                              })
                              setShowDialog(true)
                            }}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon"
                            onClick={() => handleDelete(String(event.id))}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingEvent ? t('publicOption.editEvent') : t('publicOption.addEvent')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('publicOption.eventName')} *</Label>
              <Input
                value={form.eventname}
                onChange={(e) => setForm({ ...form, eventname: e.target.value })}
                placeholder={t('common.inputPlaceholder')}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>{t('publicOption.eventKeywords')}</Label>
              <Input
                value={form.eventkeywords}
                onChange={(e) => setForm({ ...form, eventkeywords: e.target.value })}
                placeholder={t('common.inputPlaceholder')}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('publicOption.eventStartTime')}</Label>
                <Input
                  type="datetime-local"
                  value={form.eventstarttime}
                  onChange={(e) => setForm({ ...form, eventstarttime: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('publicOption.eventEndTime')}</Label>
                <Input
                  type="datetime-local"
                  value={form.eventendtime}
                  onChange={(e) => setForm({ ...form, eventendtime: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('publicOption.eventStopWords')}</Label>
              <Input
                value={form.eventstopwords}
                onChange={(e) => setForm({ ...form, eventstopwords: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={!form.eventname.trim()}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {total > 10 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('common.total')} {total} {t('common.items')}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => setPage(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
              {t('common.prev')}
            </Button>
            <span className="text-sm">{t('common.page')} {page}</span>
            <Button variant="outline" size="sm" disabled={events.length < 10}
              onClick={() => setPage(page + 1)}>
              {t('common.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
